import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Handshake, RefreshCw, Clock, User, FileText, CheckCircle, AlertCircle, 
  Loader2, Power, Mail, Calendar, Globe, ExternalLink, Send
} from 'lucide-react';

const Partenaires = () => {
  const [requests, setRequests] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [togglingService, setTogglingService] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [requestsRes, configRes, statusRes, statsRes] = await Promise.all([
        axios.get(`${API}/partenaires/requests`),
        axios.get(`${API}/partenaires/config`),
        axios.get(`${API}/services/partenaires/status`),
        axios.get(`${API}/partenaires/stats`)
      ]);
      setRequests(requestsRes.data);
      setConfig(configRes.data);
      setServiceEnabled(statusRes.data.enabled);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading partenaires:', error);
      toast.error('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleService = async () => {
    setTogglingService(true);
    try {
      const response = await axios.post(`${API}/services/partenaires/toggle`);
      setServiceEnabled(response.data.enabled);
      toast.success(response.data.enabled ? 'Service Partenaires activé' : 'Service Partenaires suspendu');
    } catch (error) {
      toast.error('Erreur lors du changement d\'état');
    } finally {
      setTogglingService(false);
    }
  };

  const forceCheck = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/partenaires/check-now`);
      toast.success('Vérification de la boîte partenaires lancée');
      setTimeout(loadData, 3000);
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setChecking(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_selection: { label: 'En attente de choix', variant: 'secondary', icon: Clock },
      form_sent: { label: 'Formulaire envoyé', variant: 'default', icon: Send },
      completed: { label: 'Traité', variant: 'success', icon: CheckCircle },
      error: { label: 'Erreur', variant: 'destructive', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending_selection;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="partenaires-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1
            className="text-2xl md:text-3xl font-bold tracking-wider uppercase mb-1"
            style={{
              fontFamily: "'Orbitron', monospace",
              color: '#00e5ff',
              textShadow: '0 0 20px rgba(0,229,255,0.3)'
            }}
          >
            PARTENAIRES
          </h1>
          <p className="text-slate-500 font-mono text-sm">
            Gestion des demandes de publication partenaires
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Toggle service */}
          <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-700">
            <Power className={`w-4 h-4 ${serviceEnabled ? 'text-green-400' : 'text-red-400'}`} />
            <span className="text-sm text-slate-400 hidden md:inline">Service</span>
            <Switch
              checked={serviceEnabled}
              onCheckedChange={toggleService}
              disabled={togglingService}
              data-testid="partenaires-service-toggle"
            />
          </div>

          {/* Force check */}
          <Button
            onClick={forceCheck}
            disabled={checking || !serviceEnabled}
            variant="outline"
            className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
            data-testid="check-partenaires-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            Vérifier
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-sky-400">{stats.total || 0}</div>
              <div className="text-sm text-slate-500">Total demandes</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-400">{stats.pending_selection || 0}</div>
              <div className="text-sm text-slate-500">En attente</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-400">{stats.completed || 0}</div>
              <div className="text-sm text-slate-500">Traités</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-xs text-slate-400 mb-1">Boîte surveillée</div>
              <div className="text-sm font-mono text-sky-300">{config?.email || '-'}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration info */}
      {config && (
        <Card className="bg-slate-900/50 border-slate-700 mb-6">
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <Handshake className="w-4 h-4 text-purple-400" />
              Configuration du service
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">CC obligatoire :</span>
                <div className="text-sky-300 font-mono text-xs mt-1">
                  {config.mandatory_cc?.join(', ') || '-'}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Domaines whitelist :</span>
                <div className="text-green-300 font-mono text-xs mt-1">
                  {config.whitelist_domains?.join(', ') || '-'}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Préfixe titre :</span>
                <div className="text-amber-300 font-mono text-xs mt-1 truncate">
                  {config.title_prefix || '-'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des demandes */}
      <Card className="bg-slate-900/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-sky-300 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Demandes récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune demande partenaire</p>
              <p className="text-xs mt-1">Les emails envoyés à {config?.email} apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                  data-testid={`partenaire-request-${request.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-200">{request.sender_name}</span>
                        <span className="text-slate-500 text-sm">({request.sender_email})</span>
                      </div>
                      
                      <div className="text-sm text-slate-300 mb-2">
                        <strong>Sujet :</strong> {request.original_subject}
                      </div>
                      
                      {request.selected_sites && request.selected_sites.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {request.selected_sites.map((site, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              <Globe className="w-3 h-3 mr-1" />
                              {site}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {request.publication_results && request.publication_results.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {request.publication_results.map((result, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              {result.status === 'success' ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-green-400" />
                                  <span className="text-slate-400">{result.site_name}</span>
                                  {result.preview_link && (
                                    <a
                                      href={result.preview_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sky-400 hover:underline flex items-center gap-1"
                                    >
                                      Voir <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3 text-red-400" />
                                  <span className="text-red-400">{result.site_name}: {result.error}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(request.status)}
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(request.created_at)}
                      </div>
                      {request.attachments_count > 0 && (
                        <div className="text-xs text-slate-400 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {request.attachments_count} pièce(s) jointe(s)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions complètes */}
      <Card className="bg-slate-900/50 border-slate-700 mt-6">
        <CardHeader>
          <CardTitle className="text-sm text-slate-400">Comment ça fonctionne ?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-500 space-y-4">
          <div>
            <h4 className="text-sky-400 font-semibold mb-2">Types de contenus acceptés :</h4>
            <ul className="space-y-1 ml-4">
              <li><strong className="text-slate-300">Article prêt à publier</strong> : Injecté tel quel dans le CMS</li>
              <li><strong className="text-slate-300">Communiqué / Dossier de presse</strong> : Réécrit par l'IA en article journalistique</li>
              <li><strong className="text-slate-300">Liste d'articles avec sites</strong> : Chaque article est placé sur le site mentionné</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sky-400 font-semibold mb-2">Workflow :</h4>
            <p className="mb-2"><strong className="text-green-400">Cas 1 - Avec consignes :</strong> Si le partenaire indique site + date dans son email → traitement automatique</p>
            <p><strong className="text-amber-400">Cas 2 - Sans consignes :</strong></p>
            <ol className="list-decimal ml-6 space-y-1">
              <li>Réception de l'email partenaire</li>
              <li>Envoi automatique du formulaire de sélection</li>
              <li>Le partenaire répond avec ses choix</li>
              <li>Articles générés/injectés en <strong>BROUILLON</strong></li>
              <li>Compte-rendu avec liens de prévisualisation</li>
            </ol>
          </div>

          <div>
            <h4 className="text-sky-400 font-semibold mb-2">Corrections post-publication :</h4>
            <ul className="space-y-1 ml-4">
              <li><span className="text-green-400">✓ Mineures (auto)</span> : Faute de nom, mot manquant, phrase à remplacer</li>
              <li><span className="text-red-400">✗ Majeures (refusées)</span> : Réécriture de paragraphes → nécessite intervention rédaction</li>
            </ul>
          </div>

          <p className="text-amber-400 mt-4 pt-4 border-t border-slate-700">
            ⚠️ Tous les emails incluent Paolo et JB en copie obligatoire
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Partenaires;
